import {
    createFormSuggestInput,
    FormSuggestInput,
    IFormSuggestInputProperties,
    IFormSuggestInputItem,
} from '@vfde-brix/ws10/form-suggest-input';
import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import {
    debounce,
    sanitizeInput,
} from '@vfde-sails/utils';
import { LoadingAnimationSize } from '@vfde-brix/ws10/loading-animation';
import {
    formValidation,
    required,
} from '@vfde-brix/ws10/validation';
import { getDevices } from '../../../api/tradeIn';
import { TRADEIN_INPUT_ID } from '../../../app/constants';
import { startAppListening } from '../../../app/listener';
import {
    RootState,
    useAppDispatch,
} from '../../../app/store';
import { selectCustomerConfig } from '../../app/slice';
import { TRADEIN_INPUT_DEBOUNCE_DURATION } from '../constants';
import { Device } from '../interfaces/api';
import {
    selectSelectedTradeInDevice,
    selectIsLoading,
    selectTradeInDevices,
} from '../selectors';
import {
    setSuggestInputValue,
    setSelectedTradeInDeviceId,
    selectIsTradeInSelected,
} from '../slice';

/**
 * Mounts the FormSuggestInput for tradeIn
 */
export const mountTradeInFormSuggestInput = (containerId: string): FormSuggestInput | null => {
    const containerElement = document.getElementById(containerId);

    /* istanbul ignore if */
    if (!containerElement) {
        return null;
    }

    const { tradeIn: tradeInOptions } = window[ADDITIONAL_PAGE_OPTIONS].promos || {};
    const stdId = TRADEIN_INPUT_ID;
    const stdLabel = tradeInOptions?.suggestInputLabel;
    const stdPlaceholder = tradeInOptions?.suggestInputPlaceholder || '';

    const dispatch = useAppDispatch();

    /**
     * Selecting a result triggers another final input event which we must ignore
     * otherwise RTK gives us a uninitialized query object very shortly which leads to
     * flickering in the components (in our case ItemSummaryCard).
     * Thats why we use the `shouldIgnoreInput` flag to detect and avoid this case.
     */
    let shouldIgnoreInput = false;

    const debouncedOnInput = debounce((value: string) => {
        if (shouldIgnoreInput) {
            return;
        }

        const sanitizedValue = sanitizeInput(value);

        dispatch(setSuggestInputValue(sanitizedValue));
        dispatch(getDevices.initiate(sanitizedValue));
    }, TRADEIN_INPUT_DEBOUNCE_DURATION);

    const onInput = (_event: Event, value: string) => {
        shouldIgnoreInput = false;
        debouncedOnInput(value);
    };

    const onChange = (event: Event, value: string) => {
        // onChange will be triggered on regular change events
        // and when the user clicks the 'x' icon to reset the input value
        onInput(event, value);
    };

    const onSelect = (_event: Event, item: IFormSuggestInputItem) => {
        shouldIgnoreInput = true;
        dispatch(setSelectedTradeInDeviceId(item.id!));
    };

    const formSuggestInputProperties: Partial<IFormSuggestInputProperties> = {
        stdId,
        stdName: stdId,
        stdLabel,
        optDisabled: false,
        optDisableFiltering: true,
        optWithLoadingAnimation: true,
        optLoadingAnimationSize: LoadingAnimationSize.Medium,
        containerLabel: {
            business: NO_PATTERN_BUSINESS_LOGIC,
        },
        containerInput: {
            stdPlaceholder: stdPlaceholder,
            // the optType is needed here
            // to attach the correct event (blur instead of change)
            // for validation otherwise it won't work
            optType: 'text',
        },
        business: {
            onInput,
            onChange,
            onSelect,
            validation: [
                {
                    validator: required,
                    errorKey: 'tradeInInputRequired',
                },
            ],
        },
    };

    const formSuggestInput = createFormSuggestInput(containerElement, formSuggestInputProperties as IFormSuggestInputProperties);

    formValidation({
        formFields: [
            formSuggestInput,
        ],
    });

    listenForUpdates(formSuggestInput);

    return formSuggestInput;
};

const listenForUpdates = (formSuggestInput: FormSuggestInput) => {
    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectCustomerConfig(currentState).isHardwareOnly !== selectCustomerConfig(previousState).isHardwareOnly
            || selectIsTradeInSelected(currentState) !== selectIsTradeInSelected(previousState)
            || selectSelectedTradeInDevice(currentState) !== selectSelectedTradeInDevice(previousState)
        ,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            toggleFormSuggestInput(formSuggestInput, state);
        },
    });

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectIsLoading(currentState) !== selectIsLoading(previousState)
        ,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const isLoading = selectIsLoading(state);

            formSuggestInput?.toggleLoadingAnimation(isLoading);
        },
    });

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectTradeInDevices(currentState) !== selectTradeInDevices(previousState)
        ,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            const tradeInDevices = selectTradeInDevices(state);

            if (!tradeInDevices) {
                // This early return is needed to avoid a scenario when `tradeInDevices` is undefined.
                // This can happen, when the suggestInputValue is changed, but no new `getDevices.initiate` is dispatched yet.
                // In that case the query in the selector will have the status `uninitialized` and data will be undefined.
                return;
            }

            updateFormSuggestInputResults(formSuggestInput, tradeInDevices);
        },
    });
};

const toggleFormSuggestInput = (formSuggestInput: FormSuggestInput, state: RootState) => {
    const customerConfig = selectCustomerConfig(state);
    const { isHardwareOnly } = customerConfig;
    const isTradeInSelected = selectIsTradeInSelected(state);
    const selectedTradeInDevice = selectSelectedTradeInDevice(state);
    const shouldShowFormSuggestInput = !isHardwareOnly && isTradeInSelected && !selectedTradeInDevice;

    resetFormSuggestInput(formSuggestInput);

    formSuggestInput?.toggleContainer(!shouldShowFormSuggestInput, true);
};

/**
 * Clears the input value and result list of the FormSuggestInput
 */
const resetFormSuggestInput = (formSuggestInput: FormSuggestInput): void => {
    formSuggestInput.clearInputValue();
    formSuggestInput.updateResultList([]);
    formSuggestInput.update({ stdErrorKey: undefined }, true);
};

/**
 * Updates the result list of the FormSuggestInput
 * with the tradeIn device list
 */
const updateFormSuggestInputResults = (
    formSuggestInput: FormSuggestInput,
    devices: Device[],
): void => {
    const items = convertTradeInDevicesToFormSuggestInputItems(devices);
    formSuggestInput.updateResultList(items);
};

const convertTradeInDevicesToFormSuggestInputItems = (devices: Device[]): IFormSuggestInputItem[] =>
    devices
        .map(device => ({ id: device.id, text: device.name! }))
        .toSorted((deviceA, deviceB) => deviceA.text!.localeCompare(deviceB.text!))
    ;
